<template>
  <div
    class="error-aggregator"
    :class="{
      'no-errors': isNoErrors,
    }"
  >
    <div class="counter" @click="handleCounterClick">
      <ui-icon
        v-if="isNoErrors"
        :color="colors.grass"
        size="12"
        icon="fa fa-check-circle"
      />
      <ui-icon v-else :color="colors.red" size="12" icon="fa fa-exclamation-circle" />
      <span class="counter-text mr-4">
        {{ t('Errors occurred: {0}', [errorCount]) }}
      </span>
    </div>
    <div class="popup">
      <div
        class="popup-inner"
        :class="{
          'popup-inner_active': popupOpen,
        }"
      >
        <div class="list-wrp">
          <ul class="list">
            <li v-for="(error, index) in errorList" :key="index" class="list-item pb-8">
              <slot name="list-item" :item="error">
                <span v-html="error.message"></span>
              </slot>
            </li>
          </ul>
          <div v-if="popupOpen" class="list-close" @click="handleListClose">
            <ui-icon :color="colors.white" size="24" icon="fas fa-times-circle" />
          </div>
          <div class="arrow"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { useI18n, useColors } from '@/composables';

export type AggregatedErrorItem = { message: string; type?: string };

const props = withDefaults(
  defineProps<{
    modelValue?: string | string[] | AggregatedErrorItem[] | null;
    active?: boolean;
    fixErrorCount?: boolean;
  }>(),
  {
    active: false,
    fixErrorCount: true,
  },
);

const emit = defineEmits<{
  'update:modelValue': [value: string | string[] | AggregatedErrorItem[] | null];
  'update:active': [value: boolean];
}>();

const { t } = useI18n();
const colors = useColors();

const popupOpen = ref(props.active);

watch(
  () => props.active,
  nextActive => {
    popupOpen.value = nextActive;
  },
);

const stringToError = (message: string): AggregatedErrorItem => ({ message });

const errorList = computed((): AggregatedErrorItem[] => {
  const rawModelValue = props.modelValue;
  if (rawModelValue == null || rawModelValue === '') return [];
  if (typeof rawModelValue === 'string') return [stringToError(rawModelValue)];
  if (Array.isArray(rawModelValue)) {
    return rawModelValue.map(stringOrItem =>
      typeof stringOrItem === 'string'
        ? stringToError(stringOrItem)
        : { ...stringOrItem, message: stringOrItem.message ?? '' },
    );
  }
  return [];
});

const errorCount = computed(() => {
  if (props.fixErrorCount && errorList.value.length > 1) {
    return errorList.value.length - 1;
  }
  return errorList.value.length;
});

const isNoErrors = computed(() => errorList.value.length === 0);

const handleCounterClick = () => {
  if (!isNoErrors.value) {
    popupOpen.value = !popupOpen.value;
    emit('update:active', popupOpen.value);
  }
};

const handleListClose = () => {
  popupOpen.value = false;
  emit('update:active', false);
};
</script>

<style lang="scss" scoped>
.error-aggregator:not(.no-errors) .counter:hover ~ .popup .popup-inner {
  display: flex;
}

.counter {
  cursor: pointer;
  color: var(--color-red);
  display: flex;
  align-items: center;
}

.counter-text {
  vertical-align: sub;
  white-space: nowrap;
  margin-left: 4px;
}

.no-errors {
  .counter {
    color: var(--color-grass);
  }
}

.list {
  list-style-type: none;
  padding: 0;
  padding-right: 40px;
  margin-right: -36px;
  overflow: auto;
  height: 100%;

  &-item {
    &:nth-child(2n) {
      color: var(--color-yellow);
    }
  }
}

.popup {
  position: absolute;
  bottom: 50px;
  left: 8px;
  display: flex;
  max-height: calc(100% - 100px);
}

.popup-inner {
  max-height: 100%;
  bottom: 10px;
  color: var(--color-white);
  display: none;
  background-color: var(--color-red);
  border: 1px var(--color-red) solid;
  border-radius: 4px;
  padding: 16px 16px 8px;
  z-index: 100;
  min-width: 300px;
  max-width: calc(100% - 16px);
  flex-grow: 1;
  flex-shrink: 0;
  position: relative;

  &_active {
    display: flex;
  }
}

.list-wrp {
  overflow-x: hidden;
}

.list-item {
  position: relative;
}

.list-close {
  position: absolute;
  top: 10px;
  right: 10px;
  cursor: pointer;
}

.arrow {
  height: 12px;
  width: 12px;
  position: absolute;
  left: 15px;
  bottom: -7px;
  background: var(--color-red);
  transform: rotate(45deg);
}
</style>
